import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from '../../../../hooks/useTranslations';
import ExpandableImage from '../ExpandableImage/ExpandableImage';
import { SmallHeader } from '../../../ui/Header/Header';
import Divider from '../../../ui/Divider/Divider';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickSubTypes,
  NarvarClickTypes,
} from '../../../../hooks/Analytics';
import useOrderTracking from '../../../../hooks/useOrderTracking';
import { LayoutTree } from '../../DynamicLayout';
import { clsx } from 'clsx';
import styles from './Vpod.module.css';

const { FeatureLayout } = LayoutTree;

interface VPODProps {
  featureName?: string;
  layout: LayoutTree.FeatureLayout;
}

const breakpoint = 768;

const VPOD = ({ featureName, layout }: VPODProps) => {
  const landscape = layout === FeatureLayout.LANDSCAPE;
  const logEvent = useAnalyticsPost();
  const {
    packEddPanelProofOfDelivery,
    packEddPanelProofOfDeliveryContractImage,
    packEddPanelProofOfDeliveryExpandImage,
  } = useTranslations();
  const container = useRef<HTMLDivElement | null>(null);
  const [isValidImage, setIsValidImage] = useState(false);
  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const handleExpand = () => {
    logEvent({
      clickCategory: NarvarClickSubTypes.VPOD_EXPAND,
      clickSubType: NarvarClickSubTypes.VPOD_EXPAND,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_BUNDLE_VPOD,
      eventFeature: NarvarEventComponents.NTH_BUNDLE_VPOD,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BUNDLE_VPOD,
      trackLayout: layout,
    });
    // Scroll to the top of the proof of delivery component
    if (window.innerWidth < breakpoint && container.current) {
      window.scrollTo({
        behavior: 'smooth',
        top:
          container.current.getBoundingClientRect().top +
          window.scrollY -
          document.getElementsByTagName('header')[0].clientHeight,
      });
    }
  };

  const handleContract = () => {
    logEvent({
      clickCategory: NarvarClickSubTypes.VPOD_CONTRACT,
      clickSubType: NarvarClickSubTypes.VPOD_CONTRACT,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_BUNDLE_VPOD,
      eventFeature: NarvarEventComponents.NTH_BUNDLE_VPOD,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BUNDLE_VPOD,
      trackLayout: layout,
    });
  };

  const url = currentShipment?.verificationContent?.url;

  useEffect(() => {
    if (!url) return;

    logEvent({
      eventComponent: NarvarEventComponents.NTH_BUNDLE_VPOD,
      eventFeature: NarvarEventComponents.NTH_BUNDLE_VPOD,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BUNDLE_VPOD,
      trackLayout: layout,
    });
  }, [url, layout, featureName, logEvent]);

  if (!url) return null;

  return (
    <div
      className={clsx({
        [styles.containerOuterHidden]: !isValidImage,
      })}
      aria-hidden={!isValidImage}
    >
      {!landscape && <Divider />}
      <div className={styles.container} ref={container}>
        <SmallHeader as="h3" containerCustomStyle={{ marginBottom: '0.5rem' }}>
          {packEddPanelProofOfDelivery}
        </SmallHeader>
        <ExpandableImage
          featureName={featureName}
          src={url}
          alt={packEddPanelProofOfDelivery ?? ''}
          expandAriaLabel={packEddPanelProofOfDeliveryExpandImage ?? ''}
          contractAriaLabel={packEddPanelProofOfDeliveryContractImage ?? ''}
          onExpand={handleExpand}
          onContract={handleContract}
          onValidImageDetermined={(valid: boolean) => {
            setIsValidImage(valid);
          }}
        />
      </div>
      {landscape && <Divider />}
    </div>
  );
};

export default VPOD;
